import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AuthError } from "@/api/errors";
import { authService } from "@/services/authService";
import type { AuthenticatedUser, RoleName } from "@/types/domain";

type Status = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  user: AuthenticatedUser | null;
  status: Status;
  login: (email: string, password: string, remember?: boolean) => Promise<AuthenticatedUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
  hasRole: (role: RoleName) => boolean;
  hasAnyRole: (roles: RoleName[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [status, setStatus] = useState<Status>("loading");

  const refresh = useCallback(async () => {
    try {
      const me = await authService.me();
      setUser(me);
      setStatus("authenticated");
    } catch (err) {
      if (err instanceof AuthError) {
        setUser(null);
        setStatus("anonymous");
        return;
      }
      // Network or server error — treat as anonymous but do not throw.
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback<AuthContextValue["login"]>(async (email, password, remember) => {
    const me = await authService.login(email, password, remember);
    setUser(me);
    setStatus("authenticated");
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      login,
      logout,
      refresh,
      can: (perm) => !!user && user.permissions.includes(perm),
      hasRole: (role) => !!user && user.roles.includes(role),
      hasAnyRole: (roles) => !!user && roles.some((r) => user.roles.includes(r)),
    }),
    [user, status, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
